import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL, waUrl } from "../lib/constants";

const GOLD = "#c9a227";
const BLACK = "#0a0a0a";
const CALENDLY_BLOCK_A = "https://calendly.com/iamtatvak/sleep-anxiety-broadcast-block-a-euro-gulf";
const CALENDLY_BLOCK_B = "https://calendly.com/iamtatvak/sleep-anxiety-broadcast-block-a-euro-gulf-clone";
const STRIPE_SINGLE_30 = "https://buy.stripe.com/aFa5kE6PG4q40YBbyZ5Vu02"; // $29.99 single-seat — PAY-FIRST flow (redirects to /thank-you?sku=single)
const STRIPE_BUNDLE_99 = "https://buy.stripe.com/cNi6oIa1Se0E8r332t5Vu00";
const STRIPE_MEMBERSHIP_79 = "https://buy.stripe.com/4gM5kE8XO09O9v732t5Vu01";
const CALENDLY_URL = CALENDLY_BLOCK_A; // back-compat / secondary fallback link only — primary CTA now goes direct to Stripe

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ─── Next Session Countdown ─────────────────────────────────────────────
// Sessions run Tue/Thu/Fri (both blocks) + Sat (Block B only) in two global time blocks:
//   • Block A · Euro-Gulf  : 9:30 PM Dubai (UTC+4) = 17:30 UTC same day
//   • Block B · Americas   : 1:00 AM Dubai next day = 21:00 UTC same calendar day
// 7 total weekly slots. Countdown picks whichever is soonest from "now".
const SESSIONS = [
    // [UTC day-of-week (0=Sun…6=Sat), UTC hour, UTC minute, block, dayName, timeLabel]
    [2, 17, 30, "A", "Tuesday",  "9:30 PM Dubai / 6:30 PM London"],
    [2, 21,  0, "B", "Tuesday",  "1:00 AM Dubai / 10 PM London / 5 PM NY"],
    [4, 17, 30, "A", "Thursday", "9:30 PM Dubai / 6:30 PM London"],
    [4, 21,  0, "B", "Thursday", "1:00 AM Dubai / 10 PM London / 5 PM NY"],
    [5, 17, 30, "A", "Friday",   "9:30 PM Dubai / 6:30 PM London"],
    [5, 21,  0, "B", "Friday",   "1:00 AM Dubai / 10 PM London / 5 PM NY"],
    [6, 21,  0, "B", "Saturday", "1:00 AM Dubai / 10 PM London / 5 PM NY"],
];

const computeNextSession = () => {
    const now = new Date();
    let best = null;
    for (const [dow, hr, min, block, dayName, timeLabel] of SESSIONS) {
        const t = new Date();
        t.setUTCHours(hr, min, 0, 0);
        const diff = (dow - t.getUTCDay() + 7) % 7;
        t.setUTCDate(t.getUTCDate() + diff);
        if (t.getTime() <= now.getTime()) t.setUTCDate(t.getUTCDate() + 7);
        if (!best || t.getTime() < best.date.getTime()) {
            best = { date: t, block, dayName, timeLabel };
        }
    }
    return best;
};

const pad = (n) => String(n).padStart(2, "0");

const NextSessionCountdown = () => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const next = computeNextSession();
    const diff = Math.max(0, next.date.getTime() - now);
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return (
        <div
            data-testid="next-session-countdown"
            style={{
                margin: "0 auto 28px",
                padding: "12px 22px",
                display: "inline-flex",
                alignItems: "center",
                gap: 14,
                border: "1px solid rgba(201,162,39,0.35)",
                backgroundColor: "rgba(201,162,39,0.06)",
                borderRadius: 999,
                fontSize: 12,
                letterSpacing: "0.12em",
                flexWrap: "wrap",
                justifyContent: "center",
            }}
        >
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#e74c3c", animation: "trsBlink 1.4s ease-in-out infinite" }} />
            <span style={{ color: "#bbb", textTransform: "uppercase", fontWeight: 500 }}>
                Next session ·{" "}
                <span style={{ color: "#fff" }}>{next.dayName}</span>{" "}
                <span style={{ color: GOLD }}>Block {next.block}</span> ·{" "}
                <span style={{ color: "#ddd", textTransform: "none", letterSpacing: "0.04em" }}>{next.timeLabel}</span>
                {" · "}
            </span>
            <span style={{ color: GOLD, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {pad(hours)}:{pad(mins)}:{pad(secs)}
            </span>
        </div>
    );
};
// ────────────────────────────────────────────────────────────────────────

// Helper — get _fbp / _fbc cookies for Meta CAPI deduplication
const getCookie = (name) => {
    if (typeof document === "undefined") return null;
    const v = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return v ? v.pop() : null;
};

// Fire server-side CAPI event in parallel with browser Pixel (same event_id = deduped by Meta)
const fireCapi = (eventName, eventId, value, contentName) => {
    if (!API_BASE) return;
    try {
        fetch(`${API_BASE}/api/meta/capi`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                event_name: eventName,
                event_source_url: typeof window !== "undefined" ? window.location.href : "",
                event_id: eventId,
                fbp: getCookie("_fbp"),
                fbc: getCookie("_fbc"),
                client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                value,
                currency: "USD",
                content_name: contentName,
            }),
            keepalive: true,
        }).catch(() => {});
    } catch (e) {}
};

// ────────────────────────────────────────────────────────────────────────
// HERO VIDEO — smaller container (320px max), no native controls, force autoplay,
// tap-to-unmute overlay. Designed for Meta in-app browser + mobile Safari quirks.
const HeroVideo = () => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        // Force-play on mount — handles iOS Safari + Meta in-app browser autoplay quirks
        const tryPlay = () => {
            v.muted = true;
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        tryPlay();
        // Also retry when video is scrolled into view (some browsers throttle off-screen)
        if (typeof IntersectionObserver !== "undefined") {
            const io = new IntersectionObserver((entries) => {
                entries.forEach((e) => { if (e.isIntersecting) tryPlay(); });
            }, { threshold: 0.25 });
            io.observe(v);
            return () => io.disconnect();
        }
    }, []);
    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !v.muted;
        v.muted = next;
        setMuted(next);
        if (!next) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        }
    };
    return (
        <section style={{ padding: "0 24px 32px", maxWidth: 920, margin: "0 auto" }} data-testid="live-video">
            <div
                style={{
                    width: "100%",
                    maxWidth: 320,
                    margin: "0 auto",
                    aspectRatio: "9 / 16",
                    border: `1px solid rgba(201,162,39,0.25)`,
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,162,39,0.06)",
                    background: "#000",
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                }}
                onClick={toggleMute}
                data-testid="live-hero-video-wrap"
            >
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/brochure-assets/live-intro-poster.jpg"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    data-testid="live-hero-video"
                >
                    <source src="/brochure-assets/live-intro-captioned-web.mp4" type="video/mp4" />
                </video>
                {/* Tap-to-unmute overlay button (bottom-right) */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    data-testid="live-hero-video-mute-toggle"
                    style={{
                        position: "absolute",
                        bottom: 12,
                        right: 12,
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        background: "rgba(0,0,0,0.55)",
                        border: `1px solid rgba(201,162,39,0.5)`,
                        color: GOLD,
                        fontSize: 16,
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        backdropFilter: "blur(6px)",
                    }}
                >
                    {muted ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                    )}
                </button>
            </div>
            <p style={{ marginTop: 12, textAlign: "center", color: "rgba(255,255,255,0.55)", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                Tap to unmute · 90 seconds with Tatva
            </p>
        </section>
    );
};
// ────────────────────────────────────────────────────────────────────────

/**
 * /live — Dedicated sales LP for live online sessions.
 * Destination for Purchase ads (NOT /sleep — that's the lead-capture IT funnel).
 * Pixel fires ViewContent on load (paid-traffic optimisation signal).
 * Calendly link → /thank-you redirect → Purchase event fires.
 * Compliance: uses Tatva's preferred current terminology (he's ISTA/IPHM/Berklee-credentialed).
 */
const Live = () => {
    const viewFiredRef = useRef(false);

    // Effect 1: Fire ViewContent ONCE per page session (guarded against StrictMode double-invoke)
    useEffect(() => {
        document.title = "Live Online Sound Recovery Sessions — The Reset Sanctuary";
        if (viewFiredRef.current) return;
        viewFiredRef.current = true;

        const eventId = `vc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            try {
                window.fbq("track", "ViewContent", {
                    content_name: "Sleep & Anxiety Broadcast",
                    content_category: "Live Broadcast LP",
                    value: 29.99,
                    currency: "USD",
                    eventID: eventId,
                });
            } catch (e) {}
        }
        fireCapi("ViewContent", eventId, 29.99, "Sleep & Anxiety Broadcast");
    }, []);

    // Effect 2: Scroll-depth tracking — MUST attach on every mount (not guarded).
    // Uses both scroll listener AND a polling fallback to handle iOS Safari /
    // in-app webview / Facebook browser quirks where passive scroll events drop.
    useEffect(() => {
        const fired = { 25: false, 50: false, 75: false, 100: false };
        let ticking = false;
        const measure = () => {
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
            const docHeight = Math.max(
                document.documentElement.scrollHeight || 0,
                document.body.scrollHeight || 0,
                document.documentElement.offsetHeight || 0,
                document.body.offsetHeight || 0
            );
            if (docHeight <= 0) return;
            const pct = Math.min(100, Math.round(((scrollY + viewport) / docHeight) * 100));
            [25, 50, 75, 100].forEach((mark) => {
                if (pct >= mark && !fired[mark]) {
                    fired[mark] = true;
                    if (typeof window.fbq === "function") {
                        try {
                            window.fbq("trackCustom", `ScrollDepth_${mark}`, {
                                content_name: "Sleep & Anxiety Broadcast LP",
                                depth_pct: mark,
                            });
                        } catch (e) {}
                    }
                }
            });
        };
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { measure(); ticking = false; });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("touchmove", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        // Polling fallback: catches mobile webviews / iOS where scroll events get dropped.
        // 750ms is cheap and ensures we never miss a scroll threshold.
        const pollId = setInterval(measure, 750);
        // Fire once on mount
        measure();
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("touchmove", onScroll);
            window.removeEventListener("resize", onScroll);
            clearInterval(pollId);
        };
    }, []);

    const handleReserveClick = () => {
        const eventId = `ic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            try {
                window.fbq("track", "InitiateCheckout", {
                    content_name: "Sleep & Anxiety Broadcast",
                    value: 29.99,
                    currency: "USD",
                    eventID: eventId,
                });
            } catch (e) {}
        }
        fireCapi("InitiateCheckout", eventId, 29.99, "Sleep & Anxiety Broadcast");
    };

    // PAY-FIRST: direct to Stripe checkout for $29.99 (single seat).
    // Wraps fbp+fbc+ua into client_reference_id so the Stripe webhook can pass them
    // through to the server-side Purchase CAPI event for max EMQ.
    // Buyers land on /thank-you?sku=single and pick their session block post-payment.
    const handleReserveStripeClick = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const eventId = `ic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            try {
                window.fbq("track", "InitiateCheckout", {
                    content_name: "Sleep & Anxiety Broadcast",
                    value: 29.99,
                    currency: "USD",
                    eventID: eventId,
                });
            } catch (err) {}
        }
        fireCapi("InitiateCheckout", eventId, 29.99, "Sleep & Anxiety Broadcast");
        let url = STRIPE_SINGLE_30;
        try {
            const fbp = getCookie("_fbp");
            const fbc = getCookie("_fbc");
            const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 80);
            const payload = { fbp, fbc, ua, t: Date.now() };
            const json = JSON.stringify(payload);
            const crid = btoa(unescape(encodeURIComponent(json)))
                .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
            url = `${STRIPE_SINGLE_30}?client_reference_id=${crid}`;
        } catch (err) { /* fallback to baseUrl */ }
        if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleAddToCart = (sku, value) => {
        const eventId = `atc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            try {
                window.fbq("track", "AddToCart", {
                    content_name: sku,
                    value,
                    currency: "USD",
                    eventID: eventId,
                });
            } catch (e) {}
        }
        fireCapi("AddToCart", eventId, value, sku);
    };

    // Pack fbp+fbc+ua into Stripe client_reference_id so the webhook can hash them into the Purchase CAPI event.
    // Boosts Meta Event Match Quality from ~5.7/10 → 8+/10 (+19% attributed conversions per Meta's own diagnostic).
    const handleStripeClick = (e, baseUrl, sku, value) => {
        e.preventDefault();
        handleAddToCart(sku, value);
        let url = baseUrl;
        try {
            const fbp = getCookie("_fbp");
            const fbc = getCookie("_fbc");
            const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 80);
            const payload = { fbp, fbc, ua, t: Date.now() };
            const json = JSON.stringify(payload);
            const crid = btoa(unescape(encodeURIComponent(json)))
                .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
            url = `${baseUrl}?client_reference_id=${crid}`;
        } catch (err) { /* fallback to baseUrl */ }
        if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <main
            data-testid="live-page"
            style={{
                backgroundColor: BLACK,
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                minHeight: "100vh",
            }}
        >
            <style>{`@keyframes trsBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }`}</style>
            {/* HERO */}
            <section style={{ padding: "32px 24px 24px", maxWidth: 920, margin: "0 auto", textAlign: "center" }} data-testid="live-hero">
                <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ width: 56, margin: "0 auto 16px", display: "block" }} />
                <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px" }}>
                    Live Online · Tuesday · Thursday · Friday
                </p>
                <NextSessionCountdown />
                <h1 className="font-serif" style={{ fontSize: "clamp(32px, 6vw, 56px)", fontWeight: 400, margin: "0 0 14px", lineHeight: 1.08, letterSpacing: "-0.02em" }}>
                    60 minutes. Your bed.<br/>
                    <span style={{ color: GOLD, fontStyle: "italic" }}>Real sleep.</span>
                </h1>
                <p style={{ color: "#ccc", fontSize: 16, lineHeight: 1.55, maxWidth: 600, margin: "0 auto 24px", fontWeight: 300 }}>
                    Live, lossless sound &mdash; guided by Tatva K from Dubai. Headphones on, 60 minutes, sleep follows.
                </p>
                <a
                    href={STRIPE_SINGLE_30}
                    onClick={handleReserveStripeClick}
                    data-testid="live-cta-hero"
                    style={{
                        display: "inline-block",
                        padding: "16px 36px",
                        backgroundColor: GOLD,
                        color: BLACK,
                        textDecoration: "none",
                        fontSize: 13,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        border: `1px solid ${GOLD}`,
                    }}
                >
                    Reserve Your Spot — $29.99
                </a>
                <p style={{ margin: "10px 0 0", fontSize: 11.5, color: "#888", fontWeight: 400, letterSpacing: "0.04em" }}>
                    Secure checkout · Apple Pay / card · 30 seconds
                </p>
                <p style={{ color: "#888", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", margin: "14px 0 0", fontWeight: 500 }}>
                    Unlimited Live Access · Lossless Broadcast · Live, not recorded
                </p>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#9a9a9a", fontWeight: 400, letterSpacing: "0.02em" }} data-testid="live-trust-badge-hero">
                    <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> Money-back if we fail to deliver —{" "}
                    <Link to="/refund" style={{ color: "#bbb", textDecoration: "underline", textUnderlineOffset: 2 }} data-testid="live-trust-badge-hero-link">
                        see Refund Policy
                    </Link>
                </p>

                {/* HOW IT WORKS — 3-step trust strip (closes the "what happens after I pay?" gap for cold ad traffic) */}
                <div data-testid="live-how-it-works" style={{ margin: "26px auto 0", maxWidth: 520, border: `1px solid rgba(201,162,39,0.18)`, padding: "16px 18px", backgroundColor: "rgba(201,162,39,0.025)" }}>
                    <p style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 12px", textAlign: "center" }}>
                        How it works · 60 seconds
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                            <span style={{ color: GOLD, fontFamily: "Playfair Display, serif", fontSize: 18, fontStyle: "italic", lineHeight: 1, minWidth: 22 }}>01</span>
                            <p style={{ margin: 0, color: "#ddd", fontSize: 13, lineHeight: 1.55, fontWeight: 300 }}>
                                <strong style={{ color: "#fff", fontWeight: 500 }}>Pay $29.99</strong> on Stripe (Apple Pay / card · 30 seconds, secure).
                            </p>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                            <span style={{ color: GOLD, fontFamily: "Playfair Display, serif", fontSize: 18, fontStyle: "italic", lineHeight: 1, minWidth: 22 }}>02</span>
                            <p style={{ margin: 0, color: "#ddd", fontSize: 13, lineHeight: 1.55, fontWeight: 300 }}>
                                <strong style={{ color: "#fff", fontWeight: 500 }}>Pick your session</strong> — Tuesday, Thursday or Friday. Block A (Euro/Gulf) or Block B (Americas).
                            </p>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                            <span style={{ color: GOLD, fontFamily: "Playfair Display, serif", fontSize: 18, fontStyle: "italic", lineHeight: 1, minWidth: 22 }}>03</span>
                            <p style={{ margin: 0, color: "#ddd", fontSize: 13, lineHeight: 1.55, fontWeight: 300 }}>
                                <strong style={{ color: "#fff", fontWeight: 500 }}>Zoom link emailed instantly.</strong> Headphones on, lie in bed, press play. Sleep follows.
                            </p>
                        </div>
                    </div>
                </div>
                <p data-testid="live-hero-testimonial" style={{ margin: "16px auto 0", maxWidth: 460, color: "#ddd", fontSize: 13.5, lineHeight: 1.5, fontStyle: "italic", fontWeight: 300 }}>
                    &ldquo;First time I slept through the night in three weeks. Genuinely &mdash; thank you.&rdquo;
                    <span style={{ display: "block", color: "#888", fontStyle: "normal", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 8 }}>
                        Maria K. &middot; London &middot; verified attendee
                    </span>
                </p>
            </section>

            {/* HERO VIDEO — Tatva's 90-sec vertical intro (9:16, autoplay muted loop, smaller container, clean cinematic — no native controls) */}
            <HeroVideo />
            

            {/* CREDENTIALS BAR — pulled up for cold-traffic trust anchor */}
            <section style={{ padding: "30px 24px 36px", backgroundColor: "#0d0d0d", borderTop: `1px solid rgba(201,162,39,0.15)`, borderBottom: `1px solid rgba(201,162,39,0.15)` }} data-testid="live-creds">
                <p style={{ color: GOLD, fontSize: 10.5, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px", textAlign: "center" }}>
                    Berklee · ISTA · IPHM · Featured on Insight Timer · 150M+ Streams · BBC · TEDx · Rolling Stone
                </p>
            </section>

            {/* TESTIMONIALS — pulled up to land in mobile screens 2-3, where ad traffic still scrolls */}
            <section style={{ padding: "40px 24px", backgroundColor: "#0d0d0d", borderBottom: `1px solid rgba(201,162,39,0.15)` }} data-testid="live-testimonials">
                <div style={{ maxWidth: 1040, margin: "0 auto" }}>
                    <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 16px", textAlign: "center" }}>
                        What People Say
                    </p>
                    <h2 className="font-serif" style={{ fontSize: "clamp(26px, 4vw, 34px)", fontWeight: 400, margin: "0 0 40px", lineHeight: 1.15, textAlign: "center", color: "#fff" }}>
                        They lay down. <span style={{ color: GOLD, fontStyle: "italic" }}>Then they slept.</span>
                    </h2>

                    {/* Text quote cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
                        {[
                            { q: "I haven't slept this deeply in three years. I don't know what he did but my body remembers.", name: "Sachin · Dubai" },
                            { q: "Tatva put me out cold in twenty minutes. I woke up Sunday morning feeling like a different person.", name: "Martyn · Dubai" },
                            { q: "I was sceptical. Now I'm a regular. Every session takes me somewhere I can't get to on my own.", name: "Daniel · UK" },
                        ].map((t, i) => (
                            <div key={i} style={{ padding: "22px 22px", border: `1px solid rgba(201,162,39,0.2)`, backgroundColor: "rgba(201,162,39,0.025)" }}>
                                <p style={{ color: "#ddd", fontSize: 14.5, lineHeight: 1.65, margin: "0 0 14px", fontWeight: 300, fontStyle: "italic" }}>
                                    &ldquo;{t.q}&rdquo;
                                </p>
                                <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", margin: 0, fontWeight: 600 }}>
                                    — {t.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3 QUESTIONS — kills cold-traffic objections inline */}
            <section style={{ padding: "20px 24px 40px", maxWidth: 920, margin: "0 auto" }} data-testid="live-faq">
                <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 16px", textAlign: "center" }}>
                    The 3 Questions Everyone Asks
                </p>
                <h2 className="font-serif" style={{ fontSize: "clamp(26px, 4vw, 34px)", fontWeight: 400, margin: "0 0 36px", lineHeight: 1.15, textAlign: "center", color: "#fff" }}>
                    Answered straight. <span style={{ color: GOLD, fontStyle: "italic" }}>No fluff.</span>
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
                    {[
                        {
                            q: "Does this actually work over a screen?",
                            a: "Yes. The frequencies that move the brain — 432, 528, 174, 741 Hz — don't depend on a room. Your brain entrains to those tones whether they come from a concert PA or your AirPods. Lossless audio through good stereo headphones delivers ~70% of the in-room effect. Enough to feel. More than enough to sleep.",
                        },
                        {
                            q: "What do I actually need?",
                            a: "Stereo headphones — not earbuds. A bed or a deep chair. 60 minutes nobody can interrupt. Lights off if you can. Your camera and microphone will be automatically muted on entry so you can rest completely. You'll see Tatva live — he won't see you. You don't have to perform. You receive.",
                        },
                        {
                            q: "What happens in the 60 minutes?",
                            a: "5 min — guided breathwork to drop you in. 45 min — live, lossless frequency broadcast. Tuning forks, frequency generators, brainwave entrainment. Beta into Alpha into Theta. 10 min — silence. You come back gently.",
                        },
                    ].map((item, i) => (
                        <div
                            key={i}
                            data-testid={`live-faq-${i + 1}`}
                            style={{
                                padding: "22px 22px 24px",
                                border: `1px solid rgba(201,162,39,0.25)`,
                                backgroundColor: "rgba(201,162,39,0.025)",
                            }}
                        >
                            <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>
                                Q{i + 1}
                            </p>
                            <p style={{ color: "#fff", fontSize: 15, fontWeight: 500, margin: "0 0 12px", lineHeight: 1.4 }}>
                                {item.q}
                            </p>
                            <p style={{ color: "#bbb", fontSize: 13.5, lineHeight: 1.65, margin: 0, fontWeight: 300 }}>
                                {item.a}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* WHY IT WORKS */}
            <section style={{ padding: "40px 24px", backgroundColor: "#0d0d0d", borderTop: `1px solid rgba(201,162,39,0.15)`, borderBottom: `1px solid rgba(201,162,39,0.15)` }} data-testid="live-why">
                <div style={{ maxWidth: 760, margin: "0 auto" }}>
                    <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 16px", textAlign: "center" }}>
                        Why It Works
                    </p>
                    <h2 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 36px)", fontWeight: 400, margin: "0 0 24px", lineHeight: 1.15, textAlign: "center", color: "#fff" }}>
                        You don't have a discipline problem.<br/>
                        <span style={{ color: GOLD, fontStyle: "italic" }}>You have a frequency problem.</span>
                    </h2>
                    <p style={{ color: "#ccc", fontSize: 16, lineHeight: 1.75, margin: 0, fontWeight: 300 }}>
                        Magnesium, apps, pills, breathwork &mdash; they work for a week, then stop. You don't have a discipline problem. You haven't been given the right tool. Engineered frequencies don't ask for willpower. They act directly on the brainstem via the auditory pathway. <strong style={{ color: GOLD, fontWeight: 600 }}>Beta &rarr; Alpha &rarr; Theta.</strong> 60 minutes. Live. The physics take over.
                    </p>
                </div>
            </section>

            {/* WHAT TO EXPECT */}
            <section style={{ padding: "40px 24px", maxWidth: 920, margin: "0 auto" }} data-testid="live-expect">
                <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 16px", textAlign: "center" }}>
                    What Happens
                </p>
                <h2 className="font-serif" style={{ fontSize: "clamp(26px, 4vw, 34px)", fontWeight: 400, margin: "0 0 36px", lineHeight: 1.15, textAlign: "center", color: "#fff" }}>
                    Three things. Sixty minutes. Then sleep.
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
                    {[
                        { num: "01", title: "Press Play", body: "Zoom link in your inbox. Headphones on, lying in your own bed. Your camera and mic are muted automatically on entry — you watch Tatva, he doesn't watch you. No prep, no learning curve." },
                        { num: "02", title: "Receive", body: "60 minutes of engineered frequencies — live, hand-played by Tatva. Tuning forks, vibroacoustics, frequency design. Beta → Alpha → Theta." },
                        { num: "03", title: "Sleep", body: "Most people drop off in the chair. That's the work happening. You wake up with sleep your body remembers." },
                    ].map(b => (
                        <div key={b.num} style={{ padding: "24px 22px", border: `1px solid rgba(201,162,39,0.2)`, backgroundColor: "rgba(201,162,39,0.025)" }}>
                            <p style={{ color: GOLD, fontFamily: "Playfair Display, serif", fontSize: 22, margin: "0 0 8px", fontStyle: "italic" }}>{b.num}</p>
                            <p style={{ color: "#fff", fontSize: 14, letterSpacing: "0.18em", textTransform: "uppercase", margin: "0 0 10px", fontWeight: 600 }}>{b.title}</p>
                            <p style={{ color: "#bbb", fontSize: 14, lineHeight: 1.65, margin: 0, fontWeight: 300 }}>{b.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRICING + RESERVE — 3 SKU panel */}
            <section style={{ padding: "56px 24px", maxWidth: 1080, margin: "0 auto", textAlign: "center" }} data-testid="live-cta-block">
                <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 16px" }}>
                    Three Ways In
                </p>
                <h2 className="font-serif" style={{ fontSize: "clamp(30px, 4.5vw, 42px)", fontWeight: 400, margin: "0 0 36px", lineHeight: 1.1 }}>
                    Pick your level. <span style={{ color: GOLD, fontStyle: "italic" }}>Reserve in 60 seconds.</span>
                </h2>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, textAlign: "left" }}>
                    {/* SKU 1 — Single */}
                    <div data-testid="live-sku-single" style={{ padding: "26px 24px 28px", border: `1px solid rgba(201,162,39,0.25)`, backgroundColor: "rgba(201,162,39,0.02)" }}>
                        <p style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>
                            Single Broadcast
                        </p>
                        <p className="font-serif" style={{ color: "#fff", fontSize: 36, fontWeight: 400, margin: "0 0 6px", lineHeight: 1 }}>
                            $29.99
                        </p>
                        <p style={{ color: "#888", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", margin: "0 0 18px", fontWeight: 500 }}>
                            One Live Session
                        </p>
                        <ul style={{ paddingLeft: 16, margin: "0 0 22px", color: "#bbb", fontSize: 13, lineHeight: 1.7, fontWeight: 300 }}>
                            <li>One live 60-min broadcast</li>
                            <li>Block A or Block B</li>
                            <li>Instant Zoom link</li>
                        </ul>
                        <a
                            href={STRIPE_SINGLE_30}
                            onClick={handleReserveStripeClick}
                            data-testid="live-cta-sku-single"
                            style={{ display: "block", padding: "12px 16px", backgroundColor: "transparent", border: `1px solid ${GOLD}`, color: GOLD, textDecoration: "none", fontSize: 11.5, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}
                        >
                            Reserve — $29.99
                        </a>
                    </div>

                    {/* SKU 2 — 5-Seat Bundle */}
                    <div data-testid="live-sku-bundle" style={{ padding: "26px 24px 28px", border: `1px solid ${GOLD}`, backgroundColor: "rgba(201,162,39,0.06)", position: "relative" }}>
                        <span style={{ position: "absolute", top: -10, left: 18, backgroundColor: GOLD, color: BLACK, fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", padding: "3px 10px", fontWeight: 700 }}>
                            Best Value · Team
                        </span>
                        <p style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>
                            5-Seat Corporate Pass
                        </p>
                        <p className="font-serif" style={{ color: "#fff", fontSize: 36, fontWeight: 400, margin: "0 0 6px", lineHeight: 1 }}>
                            $99
                        </p>
                        <p style={{ color: "#888", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", margin: "0 0 18px", fontWeight: 500 }}>
                            5 Sessions · Save $51
                        </p>
                        <ul style={{ paddingLeft: 16, margin: "0 0 22px", color: "#bbb", fontSize: 13, lineHeight: 1.7, fontWeight: 300 }}>
                            <li>5 live broadcast seats</li>
                            <li>Use any future Block A or B</li>
                            <li>Perfect for teams, family, friends</li>
                            <li>$20 per session — best price</li>
                        </ul>
                        <a
                            href={STRIPE_BUNDLE_99}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => handleStripeClick(e, STRIPE_BUNDLE_99, "5-Seat Corporate Pass", 99)}
                            data-testid="live-cta-sku-bundle"
                            style={{ display: "block", padding: "12px 16px", backgroundColor: GOLD, color: BLACK, textDecoration: "none", fontSize: 11.5, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}
                        >
                            Get the Bundle — $99
                        </a>
                    </div>

                    {/* SKU 3 — Membership */}
                    <div data-testid="live-sku-membership" style={{ padding: "26px 24px 28px", border: `1px solid rgba(201,162,39,0.25)`, backgroundColor: "rgba(201,162,39,0.02)" }}>
                        <p style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>
                            Unlimited Membership
                        </p>
                        <p className="font-serif" style={{ color: "#fff", fontSize: 36, fontWeight: 400, margin: "0 0 6px", lineHeight: 1 }}>
                            $79<span style={{ fontSize: 14, color: "#888", fontFamily: "Inter, sans-serif" }}>/mo</span>
                        </p>
                        <p style={{ color: "#888", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", margin: "0 0 18px", fontWeight: 500 }}>
                            Unlimited Live Access
                        </p>
                        <ul style={{ paddingLeft: 16, margin: "0 0 22px", color: "#bbb", fontSize: 13, lineHeight: 1.7, fontWeight: 300 }}>
                            <li>Every Block A + Block B forever</li>
                            <li>Tue · Thu · Fri · Sat · 7 sessions/week</li>
                            <li>Cancel anytime</li>
                            <li>Best for regular sleepers</li>
                        </ul>
                        <a
                            href={STRIPE_MEMBERSHIP_79}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => handleStripeClick(e, STRIPE_MEMBERSHIP_79, "Unlimited Membership", 79)}
                            data-testid="live-cta-sku-membership"
                            style={{ display: "block", padding: "12px 16px", backgroundColor: "transparent", border: `1px solid ${GOLD}`, color: GOLD, textDecoration: "none", fontSize: 11.5, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}
                        >
                            Start Membership
                        </a>
                    </div>
                </div>

                <p style={{ color: "#888", fontSize: 12, lineHeight: 1.6, margin: "32px 0 0", fontWeight: 300 }}>
                    Unlimited live seats. Lossless broadcast. Live — not recorded. <br/>Press play tonight. Sleep deeply tomorrow.
                </p>
                <p style={{ margin: "14px 0 0", fontSize: 12, color: "#9a9a9a", fontWeight: 400, letterSpacing: "0.02em" }} data-testid="live-trust-badge-pricing">
                    <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> Money-back if we fail to deliver —{" "}
                    <Link to="/refund" style={{ color: "#bbb", textDecoration: "underline", textUnderlineOffset: 2 }} data-testid="live-trust-badge-pricing-link">
                        see Refund Policy
                    </Link>
                </p>
            </section>

            {/* FOOTER MICRO */}
            <footer style={{ padding: "30px 24px 60px", textAlign: "center", borderTop: `1px solid rgba(201,162,39,0.12)` }}>
                <p style={{ color: "#666", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", margin: "0 0 8px", fontWeight: 500 }}>
                    Hand-produced. Human. No AI.
                </p>
                <p style={{ color: "#555", fontSize: 11, margin: 0 }}>
                    <a href={waUrl("Hi, question about the live online session →")} style={{ color: GOLD, textDecoration: "none" }}>WhatsApp</a>
                    {" · "}
                    <Link to="/" style={{ color: "#777", textDecoration: "none" }}>theresetsanctuary.org</Link>
                    {" · "}
                    <Link to="/refund" style={{ color: "#777", textDecoration: "none" }} data-testid="live-footer-refund">Refund Policy</Link>
                    {" · "}
                    <Link to="/privacy" style={{ color: "#777", textDecoration: "none" }} data-testid="live-footer-privacy">Privacy Policy</Link>
                </p>
            </footer>

            {/* STICKY MOBILE CTA — always-visible Reserve button on mobile (80% of Meta traffic) — pay-first flow */}
            <a
                href={STRIPE_SINGLE_30}
                onClick={handleReserveStripeClick}
                data-testid="live-cta-sticky-mobile"
                className="live-sticky-mobile-cta"
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: GOLD,
                    color: BLACK,
                    textAlign: "center",
                    padding: "16px 20px",
                    fontSize: 13,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    textDecoration: "none",
                    borderTop: `2px solid #b8911f`,
                    zIndex: 100,
                    boxShadow: "0 -8px 24px rgba(0,0,0,0.5)",
                }}
            >
                Reserve — $29.99
            </a>
            <style>{`
                .live-sticky-mobile-cta { display: none; }
                @media (max-width: 768px) {
                    .live-sticky-mobile-cta { display: block !important; }
                    main[data-testid="live-page"] footer { padding-bottom: 90px !important; }
                }
            `}</style>
        </main>
    );
};

export default Live;
